import Konva from "konva";
import React from "react";
import { Circle, Group, Line, Rect } from "react-konva";
import { WallDoor, WallWindow, WallShape as IWallShape, WallPathShape as IWallPathShape } from "../../../../types";
import {
  getWallControlPoint,
  getWallDotPosition,
  sampleBezierSubRange,
  bezierPointAndNormal,
  bezierArcLength,
  bezierParameterAtDistance
} from "../../../../utils/bezier";
import { getWallSegments, getCurvedWallSegmentParams, getWallDash } from "../../../../utils/wall";
import { getJunctionsForWall } from "../../../../utils/wallJunction";
import { wallPathToSegments } from "../../../../utils/wallPathSegments";
import {
  DEFAULT_DOOR_COLOR,
  DEFAULT_WALL_COLOR,
  DEFAULT_WALL_THICKNESS,
  DEFAULT_WINDOW_WIDTH
} from "../../defines";
import { ShapeCommonProps } from "../../types";
import { useShape } from "../ShapeContext/useShape";

const BEZIER_SAMPLES = 40;

interface DoorLineProps {
  door: WallDoor;
  wallDx: number;
  wallDy: number;
  wallLength: number;
  thickness: number;
  draggable: boolean;
  onDragEnd: (doorId: string, newPosition: number) => void;
  onWidthChange?: (doorId: string, newWidth: number) => void;
}

const DoorLine: React.FC<DoorLineProps> = ({
  door,
  wallDx,
  wallDy,
  wallLength,
  thickness,
  draggable,
  onDragEnd,
  onWidthChange
}) => {
  if (wallLength === 0 || door.transparent) return null;

  const ux = wallDx / wallLength;
  const uy = wallDy / wallLength;

  const pendingPositionRef = React.useRef<number | null>(null);

  const handleDragStart = (e: Konva.KonvaEventObject<DragEvent>) => {
    e.cancelBubble = true;
    pendingPositionRef.current = null;
  };

  const handleDragMove = (e: Konva.KonvaEventObject<DragEvent>) => {
    e.cancelBubble = true;

    const node = e.target;
    const pos = node.position();

    const centerDist = pos.x * ux + pos.y * uy;

    const halfWidth = door.width / 2;
    const clampedDist = Math.max(halfWidth, Math.min(wallLength - halfWidth, centerDist));

    node.position({
      x: clampedDist * ux,
      y: clampedDist * uy
    });

    pendingPositionRef.current = clampedDist / wallLength;
  };

  const handleDragEnd = (e: Konva.KonvaEventObject<DragEvent>) => {
    e.cancelBubble = true;
    if (pendingPositionRef.current !== null) {
      onDragEnd(door.id, pendingPositionRef.current);
      pendingPositionRef.current = null;
    }
  };

  const handleWheel = (e: Konva.KonvaEventObject<WheelEvent>) => {
    if (!onWidthChange) return;
    e.evt.preventDefault();
    e.cancelBubble = true;
    const delta = e.evt.deltaY > 0 ? -5 : 5;
    onWidthChange(door.id, Math.max(5, Math.min(300, door.width + delta)));
  };

  const centerDist = door.position * wallLength;

  return (
    <Group
      x={centerDist * ux}
      y={centerDist * uy}
      draggable={draggable}
      onDragStart={handleDragStart}
      onDragMove={handleDragMove}
      onDragEnd={handleDragEnd}
      onWheel={handleWheel}
    >
      <Line
        points={[
          -(door.width / 2) * ux,
          -(door.width / 2) * uy,
          (door.width / 2) * ux,
          (door.width / 2) * uy
        ]}
        stroke={DEFAULT_DOOR_COLOR}
        strokeWidth={thickness / 2}
        lineCap="round"
        hitStrokeWidth={Math.max(thickness, 20)}
      />
    </Group>
  );
};

interface CurvedDoorLineProps {
  door: WallDoor;
  x1: number;
  y1: number;
  cx: number;
  cy: number;
  x2: number;
  y2: number;
  thickness: number;
}

const CurvedDoorLine: React.FC<CurvedDoorLineProps> = ({
  door,
  x1,
  y1,
  cx,
  cy,
  x2,
  y2,
  thickness
}) => {
  if (door.transparent) return null;
  const arcLen = bezierArcLength(x1, y1, cx, cy, x2, y2);
  const doorCenterDist = door.position * arcLen;
  const t = bezierParameterAtDistance(x1, y1, cx, cy, x2, y2, doorCenterDist);
  const { x, y, nx, ny } = bezierPointAndNormal(x1, y1, cx, cy, x2, y2, t);
  // Door line is along the tangent direction (perpendicular to normal)
  const tx = -ny;
  const ty = nx;
  const hw = door.width / 2;

  return (
    <Line
      points={[
        x - hw * tx - x1,
        y - hw * ty - y1,
        x + hw * tx - x1,
        y + hw * ty - y1
      ]}
      stroke={DEFAULT_DOOR_COLOR}
      strokeWidth={thickness / 2}
      lineCap="round"
    />
  );
};

interface WindowLineProps {
  window: WallWindow;
  wallDx: number;
  wallDy: number;
  wallLength: number;
  thickness: number;
  draggable: boolean;
  onDragEnd: (windowId: string, newPosition: number) => void;
  onWidthChange?: (windowId: string, newWidth: number) => void;
}

const WindowLine: React.FC<WindowLineProps> = ({
  window: win,
  wallDx,
  wallDy,
  wallLength,
  thickness,
  draggable,
  onDragEnd,
  onWidthChange
}) => {
  if (wallLength === 0) return null;

  const ux = wallDx / wallLength;
  const uy = wallDy / wallLength;

  const pendingPositionRef = React.useRef<number | null>(null);

  const handleDragStart = (e: Konva.KonvaEventObject<DragEvent>) => {
    e.cancelBubble = true;
    pendingPositionRef.current = null;
  };

  const handleDragMove = (e: Konva.KonvaEventObject<DragEvent>) => {
    e.cancelBubble = true;
    const node = e.target;
    const pos = node.position();
    const centerDist = pos.x * ux + pos.y * uy;
    const halfWidth = win.width / 2;
    const clampedDist = Math.max(halfWidth, Math.min(wallLength - halfWidth, centerDist));
    node.position({ x: clampedDist * ux, y: clampedDist * uy });
    pendingPositionRef.current = clampedDist / wallLength;
  };

  const handleDragEnd = (e: Konva.KonvaEventObject<DragEvent>) => {
    e.cancelBubble = true;
    if (pendingPositionRef.current !== null) {
      onDragEnd(win.id, pendingPositionRef.current);
      pendingPositionRef.current = null;
    }
  };

  const handleWheel = (e: Konva.KonvaEventObject<WheelEvent>) => {
    if (!onWidthChange) return;
    e.evt.preventDefault();
    e.cancelBubble = true;
    const delta = e.evt.deltaY > 0 ? -5 : 5;
    onWidthChange(win.id, Math.max(5, Math.min(300, win.width + delta)));
  };

  const centerDist = win.position * wallLength;

  return (
    <Group
      x={centerDist * ux}
      y={centerDist * uy}
      draggable={draggable}
      onDragStart={handleDragStart}
      onDragMove={handleDragMove}
      onDragEnd={handleDragEnd}
      onWheel={handleWheel}
    >
      <Line
        points={[
          -(win.width / 2) * ux,
          -(win.width / 2) * uy,
          (win.width / 2) * ux,
          (win.width / 2) * uy
        ]}
        stroke={DEFAULT_DOOR_COLOR}
        strokeWidth={thickness - (thickness * 20/100)}
        rx={10}
        rz={10}
        lineCap="butt"
        hitStrokeWidth={Math.max(thickness, 20)}
      />
    </Group>
  );
};

interface CurvedWindowLineProps {
  window: WallWindow;
  x1: number;
  y1: number;
  cx: number;
  cy: number;
  x2: number;
  y2: number;
  thickness: number;
}

const CurvedWindowLine: React.FC<CurvedWindowLineProps> = ({
  window: win,
  x1,
  y1,
  cx,
  cy,
  x2,
  y2,
  thickness
}) => {
  const arcLen = bezierArcLength(x1, y1, cx, cy, x2, y2);
  const winCenterDist = win.position * arcLen;
  const t = bezierParameterAtDistance(x1, y1, cx, cy, x2, y2, winCenterDist);
  const { x, y, nx, ny } = bezierPointAndNormal(x1, y1, cx, cy, x2, y2, t);
  const tx = -ny;
  const ty = nx;
  const hw = win.width / 2;

  return (
    <Line
      points={[
        x - hw * tx - x1,
        y - hw * ty - y1,
        x + hw * tx - x1,
        y + hw * ty - y1
      ]}
      stroke={DEFAULT_DOOR_COLOR}
      strokeWidth={thickness - 2}
      lineCap="butt"
    />
  );
};

export const WallShape = React.forwardRef<
  React.ComponentRef<typeof Group>,
  ShapeCommonProps
>((commonProps, ref) => {
  const { shape, onUpdate, isSelected, otherShapes } = useShape();
  const wallShape = shape as IWallShape;

  const dx = wallShape.x2 - wallShape.x;
  const dy = wallShape.y2 - wallShape.y;
  const wallLength = Math.sqrt(dx * dx + dy * dy);
  const thickness = wallShape.thickness || DEFAULT_WALL_THICKNESS;

  const ux = wallLength > 0 ? dx / wallLength : 0;
  const uy = wallLength > 0 ? dy / wallLength : 0;

  // Local state for realtime curve preview during drag
  const [dragValues, setDragValues] = React.useState<{
    curvature: number;
    curvatureCenter: number;
  } | null>(null);

  const shapeCurvature = wallShape.curvature || 0;
  const shapeCurvatureCenter = wallShape.curvatureCenter ?? 0.5;

  // Use drag values while dragging, shape values otherwise
  const curvature = dragValues?.curvature ?? shapeCurvature;
  const curvatureCenter = dragValues?.curvatureCenter ?? shapeCurvatureCenter;
  const isCurved = curvature !== 0;

  const hasDoors = wallShape.doors && wallShape.doors.length > 0;
  const dashArray = getWallDash(wallShape.dashPattern, thickness);
  const lineCapStart = wallShape.lineCapStart || 'butt';
  const lineCapEnd = wallShape.lineCapEnd || 'butt';
  const canDrag = isSelected && !shape.locked;

  // Bezier control point (curve passes through dot at t=0.5)
  const { cx, cy } = getWallControlPoint(
    wallShape.x,
    wallShape.y,
    wallShape.x2,
    wallShape.y2,
    curvature,
    curvatureCenter
  );
  const lcx = cx - wallShape.x;
  const lcy = cy - wallShape.y;

  // Dot position (where the curve actually passes through)
  const dot = getWallDotPosition(0, 0, dx, dy, curvature, curvatureCenter);

  const allWalls = React.useMemo(() => {
    const others: IWallShape[] = [];
    for (const s of otherShapes || []) {
      if (s.hidden) continue;
      if (s.type === "wall") others.push(s as IWallShape);
      else if (s.type === "wall-path") others.push(...wallPathToSegments(s as IWallPathShape));
    }
    return [wallShape, ...others];
  }, [otherShapes, wallShape]);

  const junctionFills = React.useMemo(
    () => getJunctionsForWall(wallShape, allWalls),
    [wallShape, allWalls]
  );

  // Pre-compute junction fill points to avoid .flatMap() during render
  const junctionPoints = React.useMemo(
    () =>
      junctionFills.map((fill) => ({
        points: fill.points.flatMap((p) => [p.x - wallShape.x, p.y - wallShape.y]),
        color: fill.color
      })),
    [junctionFills, wallShape.x, wallShape.y]
  );

  // Memoize wall segment calculations — these are expensive for curved walls
  const wallSegments = React.useMemo(
    () =>
      isCurved
        ? getCurvedWallSegmentParams(wallShape.doors, 0, 0, lcx, lcy, dx, dy)
        : getWallSegments(wallShape.doors, wallLength),
    [isCurved, wallShape.doors, lcx, lcy, dx, dy, wallLength]
  );

  const handleDoorDragEnd = (doorId: string, newPosition: number) => {
    const doors = (wallShape.doors || []).map((d) =>
      d.id === doorId ? { ...d, position: newPosition } : d
    );
    onUpdate({ doors } as Partial<IWallShape>);
  };

  const handleWindowDragEnd = (windowId: string, newPosition: number) => {
    const windows = (wallShape.windows || []).map((w) =>
      w.id === windowId ? { ...w, position: newPosition } : w
    );
    onUpdate({ windows } as Partial<IWallShape>);
  };

  const handleDoorWidthChange = (doorId: string, newWidth: number) => {
    const doors = (wallShape.doors || []).map((d) =>
      d.id === doorId ? { ...d, width: newWidth } : d
    );
    onUpdate({ doors } as Partial<IWallShape>);
  };

  const handleWindowWidthChange = (windowId: string, newWidth: number) => {
    const windows = (wallShape.windows || []).map((w) =>
      w.id === windowId ? { ...w, width: newWidth } : w
    );
    onUpdate({ windows } as Partial<IWallShape>);
  };

  const handleControlDragMove = (e: Konva.KonvaEventObject<DragEvent>) => {
    e.cancelBubble = true;
    const node = e.target;
    const pos = node.position();

    const perpX = -uy;
    const perpY = ux;

    const along = pos.x * ux + pos.y * uy;
    const perp = pos.x * perpX + pos.y * perpY;

    const newCenter = Math.max(0.05, Math.min(0.95, along / wallLength));
    const newCurvature = perp;

    // Clamp visual position
    const clampedAlong = newCenter * wallLength;
    node.position({
      x: clampedAlong * ux + newCurvature * perpX,
      y: clampedAlong * uy + newCurvature * perpY
    });

    // Update local state for realtime curve rendering
    setDragValues({ curvature: newCurvature, curvatureCenter: newCenter });
  };

  const handleControlDragEnd = (e: Konva.KonvaEventObject<DragEvent>) => {
    e.cancelBubble = true;
    if (dragValues) {
      onUpdate({
        curvature: Math.round(dragValues.curvature),
        curvatureCenter: Math.round(dragValues.curvatureCenter * 100) / 100
      } as Partial<IWallShape>);
    }
    setDragValues(null);
  };

  const handleControlDblClick = (e: Konva.KonvaEventObject<MouseEvent>) => {
    e.cancelBubble = true;
    onUpdate({ curvature: 0, curvatureCenter: 0.5 } as Partial<IWallShape>);
  };

  return (
    <Group {...commonProps} ref={ref}>
      {/* Invisible bbox marker so the Group's getClientRect (which
          Konva.Transformer uses to size its frame) reflects the actual
          wall extent + thickness band, instead of the empty
          width/height=0 the Group would otherwise report. */}
      <Rect
        x={Math.min(0, dx) - thickness / 2}
        y={Math.min(0, dy) - thickness / 2}
        width={Math.abs(dx) + thickness}
        height={Math.abs(dy) + thickness}
        listening={false}
      />
      {/* Junction corner fills — rendered first (below wall strokes and doors) */}
      {junctionPoints.map((jp, i) => (
        <Line
          key={`junction-${i}`}
          points={jp.points}
          fill={jp.color}
          closed
          listening={false}
        />
      ))}

      {/* Doors */}
      {isCurved
        ? wallShape.doors?.map((door) => (
            <CurvedDoorLine
              key={door.id}
              door={door}
              x1={0}
              y1={0}
              cx={lcx}
              cy={lcy}
              x2={dx}
              y2={dy}
              thickness={thickness}
            />
          ))
        : wallShape.doors?.map((door) => (
            <DoorLine
              key={door.id}
              door={door}
              wallDx={dx}
              wallDy={dy}
              wallLength={wallLength}
              thickness={thickness}
              draggable={canDrag}
              onDragEnd={handleDoorDragEnd}
              onWidthChange={canDrag ? handleDoorWidthChange : undefined}
            />
          ))}

      

      {/* Wall segments */}
      {isCurved
        ? wallSegments.map(
            ([tStart, tEnd], i) => (
              <Line
                key={`seg-${i}`}
                points={sampleBezierSubRange(0, 0, lcx, lcy, dx, dy, tStart, tEnd, BEZIER_SAMPLES)}
                stroke={wallShape.color || DEFAULT_WALL_COLOR}
                strokeWidth={thickness}
                lineCap={hasDoors ? "round" : "butt"}
                lineJoin="round"
                dash={dashArray}
              />
            )
          )
        : wallSegments.map(([start, end], i) => (
            <Line
              key={`seg-${i}`}
              points={[start * ux, start * uy, end * ux, end * uy]}
              stroke={wallShape.color || DEFAULT_WALL_COLOR}
              strokeWidth={thickness}
              lineCap={hasDoors ? "round" : "butt"}
              lineJoin="miter"
              dash={dashArray}
            />
          ))}

      {/* Round line caps at endpoints */}
      {lineCapStart === 'round' && (
        <Circle
          x={0}
          y={0}
          radius={thickness / 2}
          fill={wallShape.color || DEFAULT_WALL_COLOR}
          listening={false}
        />
      )}
      {lineCapEnd === 'round' && (
        <Circle
          x={dx}
          y={dy}
          radius={thickness / 2}
          fill={wallShape.color || DEFAULT_WALL_COLOR}
          listening={false}
        />
      )}

      {/* Windows */}
      {isCurved
        ? wallShape.windows?.map((win) => (
            <CurvedWindowLine
              key={win.id}
              window={win}
              x1={0}
              y1={0}
              cx={lcx}
              cy={lcy}
              x2={dx}
              y2={dy}
              thickness={thickness}
            />
          ))
        : wallShape.windows?.map((win) => (
            <WindowLine
              key={win.id}
              window={win}
              wallDx={dx}
              wallDy={dy}
              wallLength={wallLength}
              thickness={thickness}
              draggable={canDrag}
              onDragEnd={handleWindowDragEnd}
              onWidthChange={canDrag ? handleWindowWidthChange : undefined}
            />
          ))}

      {/* Curvature control — rendered last (on top) */}
      {canDrag && (
        <>
          {isCurved && (
            <Line
              points={[curvatureCenter * dx, curvatureCenter * dy, dot.x, dot.y]}
              stroke="#3b82f6"
              strokeWidth={1}
              dash={[4, 4]}
              listening={false}
            />
          )}
          <Circle
            x={dot.x}
            y={dot.y}
            radius={6}
            fill="#3b82f6"
            stroke="#fff"
            strokeWidth={2}
            draggable
            onDragMove={handleControlDragMove}
            onDragEnd={handleControlDragEnd}
            onDblClick={handleControlDblClick}
            onDblTap={handleControlDblClick}
          />
        </>
      )}
    </Group>
  );
});
